import { SUI_ACTIONS, type SuiActionId, type SuiPolicyConfig, type SuiRefs } from "@altheia-xyz/sui";
import type { Policy, ActionId, Asset } from "@/components/SuiPolicyEditor";

/**
 * Deployed altheia-on-Sui testnet coordinates (the proven e2e packages) +
 * the stepper-Policy -> SDK-config mapping + a client-side store for the
 * provisioned refs. The demo trades the SUI->DEEP DeepBook pool, so the vault
 * quote asset is SUI; per-asset USDC is post-demo.
 *
 * Why a localStorage store: the backend register is record-only and keeps
 * vault/policy/cap. It does NOT keep the OwnerCap (revoke/kill needs it) or the
 * agent secret (the agent signs its own swaps). For a single-machine demo we
 * persist those here, keyed by the on-chain agent_id.
 */

const env = import.meta.env as Record<string, string | undefined>;

// Multi-asset core package id (fresh republish — one id, no upgrade split).
export const CORE_LATEST = env.VITE_SUI_CORE_LATEST ?? "0x786dfa134ccb4d5144bacf0998b356aafdef0c99121d8a35ed237627d173d917";

export const SUI_DEPLOY = {
  corePkg: env.VITE_SUI_CORE_PKG ?? "0x786dfa134ccb4d5144bacf0998b356aafdef0c99121d8a35ed237627d173d917",
  adapterPkg: env.VITE_SUI_ADAPTER_PKG ?? "0xac114631b134549c489b4f00ee693b1096a38027473202865bde52b7259ee8b7",
  dbPkg: env.VITE_SUI_DB_PKG ?? "0x22be4cade64bf2d02412c7e8d0e8beea2f78828b948118d46735315409371a3c",
  registry: env.VITE_SUI_REGISTRY ?? "0xe057edccd17e284c7c1307c7d2b260c29d72d4594bed5f49785f91daa5760d12",
  pool: env.VITE_SUI_POOL ?? "0x48c95963e9eac37a316b7ae04a0deb761bcdcc2b67912374d6036e7f0e9bae9f",
  baseType: env.VITE_SUI_BASE_TYPE ?? "0x36dbef866a1d62bf7328989a10fb2f07d769f4ee587c0de4a0a256e57e0a58a8::deep::DEEP",
  quoteType: "0x2::sui::SUI",
} as const;

export const SUI_DECIMALS = 9;
/** Agent gas top-up at provision so the agent can sign its own swaps (beat 3). */
export const AGENT_GAS = 100_000_000n; // 0.1 SUI
/**
 * Value-guard floor for the SUI->DEEP market, proven in the e2e (min_out =
 * spent * rate / 1e9). The stepper's 0..1 "floor rate" assumes like-decimals;
 * cross-asset needs the market-calibrated value, so the demo uses the proven
 * constant rather than the stepper field.
 */
export const DEMO_SWAP_MIN_RATE = 15_000_000n;
/** Agent swap sizes for the demo: one inside per-tx cap, one over it. */
export const DEMO_SWAP_WITHIN = 400_000_000n; // 0.4 SUI
export const DEMO_SWAP_OVER = 600_000_000n; // 0.6 SUI

const ACTION_MAP: Record<ActionId, SuiActionId> = {
  transfer: SUI_ACTIONS.transfer,
  deepbook_swap: SUI_ACTIONS.deepbookSwap,
  deepbook_limit_order: SUI_ACTIONS.deepbookLimitOrder,
  deepbook_cancel: SUI_ACTIONS.deepbookCancel,
};

const COIN_TYPE: Record<string, string> = {
  SUI: SUI_DEPLOY.quoteType,
  USDC: env.VITE_SUI_USDC_TYPE ?? "0xa1ec7fc00a6f40db9693ad1415d0c193ad3906494428cf252621037bd7117e29::usdc::USDC",
  DEEP: SUI_DEPLOY.baseType,
};
const COIN_DECIMALS: Record<string, number> = { SUI: SUI_DECIMALS, USDC: 6, DEEP: 6 };
// Known symbol -> coin-type, else the coin string IS already a coin-type id (pasted).
const coinTypeFor = (coin: Asset): string => COIN_TYPE[coin] ?? coin;
const toBaseFor = (coin: Asset, n: number): bigint => BigInt(Math.round(n * 10 ** (COIN_DECIMALS[coin] ?? 9)));

/** Map the create-stepper policy to the SDK provision config. caps[0] is the
 * FUNDED budget asset (deposited from the wallet); the rest are capped-only
 * (the agent funds them via deposits or by acquiring them in swaps). fundAmount
 * = one day's budget so an over-cap swap is blocked by the CAP, not by an empty
 * vault (a truthful denial). */
export function policyToConfig(p: Policy): {
  cfg: SuiPolicyConfig;
  fundAmount: bigint;
  quoteType: string;
  extraCaps: { coinType: string; perTxCap?: bigint; perDayCap: bigint }[];
} {
  const budget = p.caps[0];
  const cfg: SuiPolicyConfig = {
    agentId: p.name,
    perDayCap: toBaseFor(budget.coin, budget.per_day),
    perTxCap: budget.per_tx != null ? toBaseFor(budget.coin, budget.per_tx) : undefined,
    allowedPackages: [SUI_DEPLOY.pool],
    allowedActions: p.allowed_actions.map((a) => ACTION_MAP[a]),
    expiresAtMs: BigInt(Date.now() + p.expiry_hours * 3600_000),
    swapMinRate: p.allowed_actions.includes("deepbook_swap") ? DEMO_SWAP_MIN_RATE : undefined,
  };
  const extraCaps = p.caps.slice(1).map((c) => ({
    coinType: coinTypeFor(c.coin),
    perTxCap: c.per_tx != null ? toBaseFor(c.coin, c.per_tx) : undefined,
    perDayCap: toBaseFor(c.coin, c.per_day),
  }));
  return { cfg, fundAmount: toBaseFor(budget.coin, budget.per_day), quoteType: coinTypeFor(budget.coin), extraCaps };
}

/** Build full SuiRefs from a provision result + the static deployment. */
export function buildRefs(
  prov: { vault: string; policy: string; cap: string; ownerCap: string },
  recipient: string,
): SuiRefs {
  return {
    corePkg: SUI_DEPLOY.corePkg,
    adapterPkg: SUI_DEPLOY.adapterPkg,
    dbPkg: SUI_DEPLOY.dbPkg,
    registry: SUI_DEPLOY.registry,
    vault: prov.vault,
    policy: prov.policy,
    cap: prov.cap,
    ownerCap: prov.ownerCap,
    pool: SUI_DEPLOY.pool,
    baseType: SUI_DEPLOY.baseType,
    quoteType: SUI_DEPLOY.quoteType,
    deepType: SUI_DEPLOY.baseType,
    recipient,
  };
}

// --- client-side secret/ref store (ownerCap + agent secret + refs) ---

export type StoredAgent = {
  agentId: string;
  agentAddress: string;
  name?: string; // the human label entered at creation (so a merged agent shows the name, not its id)
  refs: SuiRefs; // public object ids + OwnerCap — needed for owner actions. NO agent secret (D2).
  policy?: Record<string, unknown>; // display policy (caps/actions) so a locally-merged agent shows its terms even if the backend register hasn't landed
};

const KEY = (agentId: string) => `altheia.sui.agent.${agentId}`;

export function saveAgent(a: StoredAgent): void {
  try {
    localStorage.setItem(KEY(a.agentId), JSON.stringify(a));
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** All agents minted on this device (chain refs live here even if the backend
 * register missed them) — the per-device discovery layer for a chain-first fleet. */
export function listStoredAgents(): StoredAgent[] {
  const out: StoredAgent[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("altheia.sui.agent.")) {
        const raw = localStorage.getItem(k);
        if (raw) out.push(JSON.parse(raw) as StoredAgent);
      }
    }
  } catch { /* private mode — non-fatal */ }
  return out;
}

export function loadAgent(agentId: string): StoredAgent | null {
  try {
    const raw = localStorage.getItem(KEY(agentId));
    return raw ? (JSON.parse(raw) as StoredAgent) : null;
  } catch {
    return null;
  }
}
